import BybTrendChip from "@Components/common/byb-ds/byb-trend-chip";
import BybVrCard from "@Components/common/byb-ds/cards/byb-vr-card";
import { useProgress } from "@Context/game/progress/progress-context";
import { getImprovementShade } from "@Helpers/feedback";
import useI18n from "@i18n";
import { Box, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";

interface IProgressTrendCard {}

const CIRCLE_SIZE = 175;
const CIRCLE_CONSTANTS = {
  INNER_RADIUS_RATIO: 0.43,
  OUTER_RADIUS_RATIO: 0.5,
  CORNER_RADIUS_RATIO: 0.1,
  FONT_SIZE_RATIO: 0.13,
  SHADOW_OPACITY: 0.2,
  BACKGROUND_OPACITY: 0.75,
  START_ANGLE: 90,
} as const;

const ProgressTrendCard = ({}: IProgressTrendCard) => {
  const { i18n } = useI18n();
  const theme = useTheme();
  const { improvementPercentage, isLongTermResultsLoading, isCurrentBybIqLoading } = useProgress();
  const isPositive = improvementPercentage > 0;
  const displayPercentage = Math.min(Math.abs(improvementPercentage), 100);

  const improvementShade = getImprovementShade(improvementPercentage, theme);
  const progressData = [{ name: "progress", value: displayPercentage }];
  const backgroundData = [{ name: "background", value: 100 }];
  const shadowData = [{ name: "shadow", value: 100 }];

  const COLORS = {
    progress: "url(#arcGradient)",
    background: theme.palette.dsSurface.v300,
    shadow: theme.palette.dsSurface.v500,
  };

  const getGradientCoordinates = () => {
    const startAngle = CIRCLE_CONSTANTS.START_ANGLE;
    const endAngle = isPositive ? startAngle - displayPercentage * 3.6 : startAngle + displayPercentage * 3.6;

    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    const avgRadius = (CIRCLE_CONSTANTS.INNER_RADIUS_RATIO + CIRCLE_CONSTANTS.OUTER_RADIUS_RATIO) / 2;
    const radius = CIRCLE_SIZE * avgRadius;

    const centerX = CIRCLE_SIZE / 2;
    const centerY = CIRCLE_SIZE / 2;

    const x1 = centerX + radius * Math.cos(startRad);
    const y1 = centerY - radius * Math.sin(startRad);
    const x2 = centerX + radius * Math.cos(endRad);
    const y2 = centerY - radius * Math.sin(endRad);

    return { x1, y1, x2, y2 };
  };

  const gradientCoords = getGradientCoordinates();

  const getAngles = () => {
    if (isPositive) {
      return {
        startAngle: CIRCLE_CONSTANTS.START_ANGLE,
        endAngle: CIRCLE_CONSTANTS.START_ANGLE - displayPercentage * 3.6,
      };
    } else {
      return {
        startAngle: CIRCLE_CONSTANTS.START_ANGLE,
        endAngle: CIRCLE_CONSTANTS.START_ANGLE + displayPercentage * 3.6,
      };
    }
  };

  const angles = getAngles();

  if (isLongTermResultsLoading || isCurrentBybIqLoading) {
    return (
      <BybVrCard>
        <Stack gap={2} alignItems="center">
          <Skeleton variant="circular" width={160} height={160} />
          <Stack spacing={0} alignItems="center">
            <Skeleton variant="text" width={120} height={60} />
            <Skeleton variant="text" width={100} height={30} />
          </Stack>
          <Skeleton variant="rounded" width={80} height={30} />
        </Stack>
      </BybVrCard>
    );
  }

  return (
    <BybVrCard>
      <Stack spacing={2.5} alignItems="center" justifyContent="center" height="100%">
        <Box position="relative" width={CIRCLE_SIZE} height={CIRCLE_SIZE} display="inline-block">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <defs>
                <linearGradient
                  id="arcGradient"
                  x1={gradientCoords.x1}
                  y1={gradientCoords.y1}
                  x2={gradientCoords.x2}
                  y2={gradientCoords.y2}
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0%" stopColor={isPositive ? improvementShade?.v100 : improvementShade?.v100} />
                  <stop offset="100%" stopColor={isPositive ? improvementShade?.v300 : improvementShade?.v300} />
                </linearGradient>
              </defs>
              <Pie
                data={shadowData}
                cx="50%"
                cy="50%"
                innerRadius={CIRCLE_SIZE * CIRCLE_CONSTANTS.INNER_RADIUS_RATIO}
                outerRadius={CIRCLE_SIZE * CIRCLE_CONSTANTS.OUTER_RADIUS_RATIO}
                fill={COLORS.shadow}
                opacity={CIRCLE_CONSTANTS.SHADOW_OPACITY}
                dataKey="value"
                startAngle={0}
                endAngle={360}
                isAnimationActive={false}
                cornerRadius={CIRCLE_SIZE * CIRCLE_CONSTANTS.CORNER_RADIUS_RATIO}
                stroke="none"
              >
                {shadowData.map((entry, index) => (
                  <Cell key={`cell-shadow-${index}`} fill={COLORS.shadow} />
                ))}
              </Pie>

              <Pie
                data={backgroundData}
                cx="50%"
                cy="50%"
                innerRadius={CIRCLE_SIZE * CIRCLE_CONSTANTS.INNER_RADIUS_RATIO}
                outerRadius={CIRCLE_SIZE * CIRCLE_CONSTANTS.OUTER_RADIUS_RATIO}
                fill={COLORS.background}
                dataKey="value"
                startAngle={CIRCLE_CONSTANTS.START_ANGLE}
                endAngle={-270}
                isAnimationActive={false}
                cornerRadius={CIRCLE_SIZE * CIRCLE_CONSTANTS.CORNER_RADIUS_RATIO}
                stroke="none"
              >
                {backgroundData.map((entry, index) => (
                  <Cell key={`cell-bg-${index}`} fill={COLORS.background} />
                ))}
              </Pie>

              <Pie
                data={progressData}
                cx="50%"
                cy="50%"
                innerRadius={CIRCLE_SIZE * CIRCLE_CONSTANTS.INNER_RADIUS_RATIO}
                outerRadius={CIRCLE_SIZE * CIRCLE_CONSTANTS.OUTER_RADIUS_RATIO}
                fill={COLORS.progress}
                dataKey="value"
                startAngle={angles.startAngle}
                endAngle={angles.endAngle}
                isAnimationActive={true}
                cornerRadius={CIRCLE_SIZE * CIRCLE_CONSTANTS.CORNER_RADIUS_RATIO}
                stroke="none"
              >
                {progressData.map((entry, index) => (
                  <Cell key={`cell-progress-${index}`} fill={COLORS.progress} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
            }}
          >
            <Typography variant="textLg500" color="text.tertiary">
              {i18n("progress.trend.label")}
            </Typography>
          </Box>
        </Box>
        <Stack gap={0.75} textAlign="center">
          <Typography variant="text3xl700" color={improvementShade?.v200}>
            {improvementPercentage > 0 && "+"}
            {improvementPercentage}%
          </Typography>
          <Typography variant="textLg400" color="text.secondary">
            {i18n("progress.trend.recent_sessions")}
          </Typography>
        </Stack>
        <BybTrendChip improvementPercentage={improvementPercentage} />
      </Stack>
    </BybVrCard>
  );
};

export default ProgressTrendCard;
